import { createContext, useContext, type ComponentProps } from 'react';
import type { Components } from 'react-markdown';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import { MARKDOWN_SANITIZE_SCHEMA } from '@almanac/catalog';
import { PACKAGE_ROUTE_PREFIX } from '../lib/briefing-md';

/**
 * The package-panel opener the surrounding page provides. Carried by context
 * rather than a prop threaded into the markdown component map, so the map
 * stays a module constant (components must not be defined during render).
 */
const OpenPackageContext = createContext<((slug: string) => void) | undefined>(undefined);

const LINK_CLASS =
  'break-words font-medium text-primary decoration-primary/40 underline-offset-4 hover:underline';

/**
 * Anchor renderer: links the prettifier routed to {@link PACKAGE_ROUTE_PREFIX}
 * open the in-app package panel on a plain click; modified clicks (new
 * tab/window) keep native anchor behavior — the href is a real route, so they
 * still land on the package doc. In-page fragment links (the briefing's
 * short-version bullets point at package sections, e.g. `#quad-map-chart`)
 * stay native same-tab anchors so they scroll instead of opening a new tab.
 * Everything else opens externally.
 */
function MarkdownAnchor({ href, children }: ComponentProps<'a'>) {
  const onOpenPackage = useContext(OpenPackageContext);
  if (href?.startsWith('#')) {
    return (
      <a href={href} className={LINK_CLASS}>
        {children}
      </a>
    );
  }
  if (href?.startsWith(PACKAGE_ROUTE_PREFIX) && onOpenPackage) {
    const slug = href.slice(PACKAGE_ROUTE_PREFIX.length);
    return (
      <a
        href={href}
        className={LINK_CLASS}
        onClick={(event) => {
          if (
            event.button !== 0 ||
            event.metaKey ||
            event.ctrlKey ||
            event.shiftKey ||
            event.altKey
          ) {
            return;
          }
          event.preventDefault();
          onOpenPackage(slug);
        }}
      >
        {children}
      </a>
    );
  }
  return (
    <a href={href} target="_blank" rel="noopener noreferrer nofollow" className={LINK_CLASS}>
      {children}
    </a>
  );
}

/**
 * Briefing-tuned markdown styling: roomier lists for the briefing's long,
 * link-dense bullets than the catalog's compact `MarkdownPrimitive`, and
 * quieter link/code treatments so a row of `api#907`-style links reads as a
 * list, not a wall of underlines.
 */
const BRIEFING_COMPONENTS: Components = {
  p: ({ children }) => (
    <p className="mb-3 text-[0.9375rem] leading-relaxed text-foreground/85">{children}</p>
  ),
  ul: ({ children }) => (
    <ul className="mb-3 list-disc space-y-2.5 pl-5 text-[0.9375rem] leading-relaxed text-foreground/85 marker:text-muted-foreground/60">
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol className="mb-3 list-decimal space-y-2.5 pl-5 text-[0.9375rem] leading-relaxed text-foreground/85 marker:font-medium marker:text-muted-foreground">
      {children}
    </ol>
  ),
  li: ({ children }) => <li className="pl-1">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold text-foreground">{children}</strong>,
  code: ({ children }) => (
    <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[0.8125rem] text-foreground/90">
      {children}
    </code>
  ),
  pre: ({ children }) => (
    <pre className="my-3 overflow-x-auto rounded-lg border border-border bg-muted/60 p-3 font-mono text-[0.8125rem] leading-relaxed text-foreground/90 [&_code]:bg-transparent [&_code]:p-0">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="mb-3 border-l-2 border-border pl-3 text-sm italic text-muted-foreground">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-4 border-border" />,
  a: MarkdownAnchor,
};

/**
 * Renders one chunk of briefing markdown (a section body or the preamble)
 * with the same sanitization contract as the catalog's `MarkdownPrimitive`.
 *
 * `onOpenPackage` makes package-doc links open the in-app panel instead of
 * navigating away; without it those links fall back to plain same-tab
 * navigation.
 */
export function BriefingMarkdown({
  body,
  onOpenPackage,
}: {
  body: string;
  onOpenPackage?: (slug: string) => void;
}) {
  return (
    <OpenPackageContext.Provider value={onOpenPackage}>
      <Markdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[[rehypeSanitize, MARKDOWN_SANITIZE_SCHEMA]]}
        components={BRIEFING_COMPONENTS}
      >
        {body}
      </Markdown>
    </OpenPackageContext.Provider>
  );
}
